import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Plus, Search, Share2, SquarePen, Trash2, Tv } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { cn } from "@/v2/lib/utils";
import { timeAgo } from "@/v2/pages/Lens/components/schedule-format";

export type ChannelSummary = {
  ID: string | number;
  Configuration?: { Name?: string };
  IsShared?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

/** Row action button: small, quiet, and named after its channel for AT. */
function RowAction({
  label,
  onClick,
  children,
  destructive,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        "flex items-center justify-center rounded-md size-7 shrink-0 text-slate-400 transition-colors",
        destructive
          ? "hover:bg-destructive/10 hover:text-destructive"
          : "hover:bg-white hover:text-slate-900",
      )}
    >
      {children}
    </button>
  );
}

export function ChannelSwitcherPanel({
  channels,
  currentChannelId,
  onSelect,
  onAddChannel,
  onShareChannel,
  onEditChannel,
  onDeleteChannel,
}: {
  channels: ChannelSummary[];
  currentChannelId: string | number;
  onSelect: (id: string | number) => void;
  onAddChannel: () => void;
  onShareChannel: (id: string | number) => void;
  onEditChannel: (id: string | number) => void;
  onDeleteChannel: (id: string | number) => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const visibleChannels = useMemo(
    () =>
      (channels ?? []).filter((channel) =>
        search
          ? channel.Configuration?.Name?.toLowerCase().includes(
              search.toLowerCase(),
            )
          : true,
      ),
    [channels, search],
  );

  return (
    <div className="w-[340px]">
      <div className="flex items-center justify-between" style={{ marginBottom: "8px" }}>
        <h4 className="font-bold text-slate-900 text-sm">My Spotlights</h4>
        <span className="text-xs font-semibold text-slate-500">
          {channels?.length ?? 0} Spotlights
        </span>
      </div>

      {/* The border sits on this wrapper: v2.css's unlayered `border-width: 0`
          preflight replacement outranks any border utility on an <input>, so a
          bordered field has to be built this way (same as the header search). */}
      <div
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white h-9 px-2.5 transition-shadow focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/25"
        style={{ marginBottom: "8px" }}
      >
        <Search className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
        <input
          className="v2-search-input w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400"
          placeholder="Search Spotlights"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search Spotlights"
        />
      </div>

      <div className="max-h-72 overflow-y-auto">
        {visibleChannels.length === 0 ? (
          <div className="text-sm text-slate-500 text-center" style={{ padding: "16px 0" }}>
            No Spotlights found
          </div>
        ) : (
          visibleChannels.map((channel, i) => {
            const active = String(channel.ID) === String(currentChannelId);
            const channelLabel = channel.Configuration?.Name ?? "this Spotlight";
            return (
              // The divider lives on this wrapper rather than the row itself,
              // for the same reason as the search border above: border
              // utilities do not survive on a <button> inside .v2.
              <div
                key={channel.ID}
                className={cn(i > 0 && "border-t border-slate-100")}
              >
                {/* The row is a div, not a button: the actions on the right are
                    buttons themselves, and a button cannot nest inside one. */}
                <div
                  className={cn(
                    "flex items-center rounded-lg transition-colors",
                    active ? "bg-action-light" : "hover:bg-accent",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(channel.ID)}
                    className="flex flex-1 min-w-0 items-center gap-2.5 text-left outline-none"
                    style={{ padding: "10px 8px" }}
                  >
                    <span className="w-4 shrink-0 flex items-center justify-center">
                      {active && <Check className="w-4 h-4 text-action" aria-hidden="true" />}
                    </span>
                    <span
                      className="flex items-center justify-center rounded-md bg-slate-100 text-slate-400 shrink-0"
                      style={{ width: "36px", height: "36px" }}
                    >
                      <Tv className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-semibold text-slate-900 text-sm truncate">
                          {channelLabel}
                        </span>
                        {channel.IsShared && (
                          <span className="shrink-0 rounded-pill bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5">
                            Shared with you
                          </span>
                        )}
                      </div>
                      {channel.updatedAt && (
                        <div className="text-xs text-slate-500">
                          Updated {timeAgo(channel.updatedAt)}
                        </div>
                      )}
                    </div>
                  </button>

                  <div
                    className="flex items-center gap-0.5 shrink-0"
                    style={{ paddingRight: "6px" }}
                  >
                    {/* A channel someone shared with you is not yours to pass
                        on, which is the same reason the header hides Share on
                        a shared channel. */}
                    {!channel.IsShared && (
                      <RowAction
                        label={`Share ${channelLabel}`}
                        onClick={() => onShareChannel(channel.ID)}
                      >
                        <Share2 className="w-3.5 h-3.5" />
                      </RowAction>
                    )}
                    <RowAction
                      label={`Edit ${channelLabel}`}
                      onClick={() => onEditChannel(channel.ID)}
                    >
                      <SquarePen className="w-3.5 h-3.5" />
                    </RowAction>
                    <RowAction
                      label={`Delete ${channelLabel}`}
                      destructive
                      onClick={() => onDeleteChannel(channel.ID)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </RowAction>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <button
        type="button"
        onClick={onAddChannel}
        className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90"
        style={{ padding: "10px", marginTop: "10px" }}
      >
        <Plus className="w-4 h-4" /> New Spotlight
      </button>

      {/* The Studio is where the full, paginated channel list lives, so this is
          the way out of a popover that only shows the first screenful. */}
      <button
        type="button"
        onClick={() => navigate(PageRoutes.LensStudio)}
        className="w-full flex items-center justify-center gap-1 text-action font-bold text-sm hover:underline"
        style={{ padding: "8px", marginTop: "2px" }}
      >
        Manage all Spotlights <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
